import {
  ArrowRight,
  HelpCircle,
  Home,
  ClipboardList,
  Info,
  Keyboard,
  MessageSquare,
  MoreHorizontal,
  RotateCw,
  Sparkles,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Accordion from '@/shared/components/Accordion';
import AskAQuestionIcon from '@/assets/icons/AskAQuestionIcon';
import ChatView from './ChatView';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import Tooltip from '@/shared/components/Tooltip';
import './AlphaModal.css';
import {
  sendMessage,
  sendAudio,
  resetThreadId,
  MORNING_BRIEFING_QUESTION,
  SHIFT_HANDOVER_QUESTION,
} from '../alpha.service';
import { primeConvAiSession, clearConvAiSession } from '../services/convAiSession.service';
import { readAloud, stopReadAloud, toSpeakableText } from '@/features/alpha/services/readAloud.service';
import type { SendMessageOptions } from '@/features/alpha/types/alpha.types';
import { useRuntimeConfigContext } from '@/shared/providers/RuntimeConfigProvider';
import { CHAT_MIN_HEIGHT, CHAT_MIN_WIDTH, useChatWindowGeometry } from '@/features/alpha/hooks/useChatWindowGeometry';
import {
  startAudioRecording,
  stopAudioRecording,
  abortAudioRecording,
} from '../services/audio.service';

interface AlphaModalProps {
  /**
   * The header's X (and Escape). Closing does NOT end the chat: the owner
   * (AlphaIcon) keeps this component mounted and hidden, so the conversation is
   * there again when the chat is reopened. "Restart conversation" starts afresh.
   */
  onClose: () => void;
  /**
   * True while the chat is closed: it stays mounted — its messages and thread
   * survive — but is not shown. While hidden its keyboard shortcuts (Escape,
   * Ctrl+R, Ctrl+H) are off, the Conv-AI session refresh is paused and nothing
   * is read aloud.
   */
  hidden?: boolean;
  /**
   * A question to ask as soon as the chat opens (the Morning Briefing card's
   * "View briefing"). Sent exactly once, through the same handleSendMessage a
   * typed question uses — same endpoint, thread, auth and rendering.
   */
  initialQuestion?: string;
}

interface Message {
  text: string;
  isUser: boolean;
  payload?: any;
  timestamp: string;
}

const quickQuestions = [
  'What is the average daily revenue miles?',
  'Can you tell me the average daily revenue hours?',
  'What is the average number of daily completed trips?',
  'What is the rides-per-hour performance?',
  'What is the current pick-up on-time performance (OTP)?',
  'What is the current drop-off on-time performance (OTP)?',
];

type View = 'home' | 'chat';

const AlphaModal: React.FC<AlphaModalProps> = ({ onClose, hidden = false, initialQuestion }) => {
  const { t, i18n } = useTranslation();
  const { getConfigValue, isFeatureEnabled } = useRuntimeConfigContext();
  const convUiBaseUrl = getConfigValue<string>('web.ccagent.conv_base_url') || undefined;
  // Mic on/off without a code change. Default ON (CONFIG_DEFAULTS); an agency
  // sets web.ccagent.feature.alphaVoice=false to hide it.
  const voiceEnabled = isFeatureEnabled('web.ccagent.feature.alphaVoice');
  const [view, setView] = useState<View>('home');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  // The header speaker toggle. Unmuted by default (the chat's existing default):
  // with voice enabled, answers to SPOKEN questions are read aloud until the
  // dispatcher mutes them. Typed questions are never read aloud.
  const [isMuted, setIsMuted] = useState(false);
  const [listening, setListening] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // The window's size (its top and left edges and top-left corner resize it) and
  // position (its header drags it anywhere on screen), remembered per browser —
  // see useChatWindowGeometry. It replaces the old Maximize toggle.
  const {
    width, height, right, bottom, maxWidth, maxHeight, activeDrag,
    topEdgeHandlers, leftEdgeHandlers, cornerHandlers, moveHandlers,
  } = useChatWindowGeometry();

  // Read-aloud speaks the answer this chat already rendered, for a question asked
  // by microphone — never a second Conv-AI call. readAloudRef lets an in-flight request see a toggle made while
  // it was waiting; turnRef lets only the latest question's answer be spoken
  // (a newer question, a restart or closing the chat supersedes it).
  const readAloudOn = voiceEnabled && !isMuted;
  const readAloudRef = useRef(readAloudOn);
  const turnRef = useRef(0);

  useEffect(() => {
    readAloudRef.current = readAloudOn;
    if (!readAloudOn) stopReadAloud();
  }, [readAloudOn]);

  /** Stop speaking and make any answer still on its way silent. */
  const silenceAssistant = () => {
    turnRef.current += 1;
    stopReadAloud();
  };

  const handleSendMessage = async (
    question: string,
    timestamp: string,
    { inputMode }: SendMessageOptions = { inputMode: 'text' },
  ) => {
    if (!question.trim()) return;

    silenceAssistant();
    const turn = turnRef.current;
    setView('chat');
    const newUserMessage = { text: question, isUser: true, timestamp };
    setMessages((prev) => [...prev, newUserMessage]);
    setLoading(true);
    try {
      const response = await sendMessage(question, convUiBaseUrl);
      const aiTimestamp = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      setMessages((prev) => [
        ...prev,
        { text: '', isUser: false, payload: response, timestamp: aiTimestamp },
      ]);
      // Only a question the dispatcher SPOKE is answered aloud. inputMode is this
      // call's own argument, so an overlapping typed turn cannot flip it.
      if (inputMode === 'voice' && readAloudRef.current && turn === turnRef.current) {
        void readAloud(toSpeakableText(response, t), { lang: i18n.language, convUiBaseUrl });
      }
    } catch (error) {
      const aiTimestamp = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      setMessages((prev) => [
        ...prev,
        {
          text: t('alpha.errorGeneric'),
          isUser: false,
          timestamp: aiTimestamp,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendAudio = async (audioBlob: Blob) => {
    setView('chat');
    setLoading(true);
    try {
      const response = await sendAudio(audioBlob, convUiBaseUrl);
      const aiTimestamp = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      setMessages((prev) => [
        ...prev,
        { text: '', isUser: false, payload: response, timestamp: aiTimestamp },
      ]);
    } catch (error) {
      const aiTimestamp = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      setMessages((prev) => [
        ...prev,
        {
          text: t('alpha.errorAudio'),
          isUser: false,
          timestamp: aiTimestamp,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleFooterNav = (selectedView: View) => {
    setView(selectedView);
  };

  /** The question a home-view entry sends, stamped like a typed one. */
  const askNow = (question: string) => {
    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    void handleSendMessage(question, timestamp, { inputMode: 'text' });
  };

  // An opening question (the briefing card) is asked once, on mount. The ref
  // keeps a re-render from asking it twice; a later prop change is ignored on
  // purpose — the card opened THIS chat, and that is the one question it owns.
  const initialAskedRef = useRef(false);
  useEffect(() => {
    if (!initialQuestion || initialAskedRef.current) return;
    initialAskedRef.current = true;
    askNow(initialQuestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Interim Conv-AI auth: mint the short-lived Conv-AI session key from the BFF
  // when the chat is shown (so the next message does not pay the round-trip),
  // keep re-minting it ahead of expiry for as long as the chat is shown and the
  // Dispatcher session exists, and forget it (stopping the refresh loop) while
  // the chat is closed — closed is hidden, not unmounted, so this follows
  // `hidden`. A message sent later mints a key again anyway. The key only ever
  // lives in memory.
  useEffect(() => {
    if (hidden) return undefined;
    void primeConvAiSession();
    return () => {
      clearConvAiSession();
      // Closing the chat ends its voice too, including an answer still pending
      // (the answer itself still arrives and is there when the chat reopens).
      turnRef.current += 1;
      stopReadAloud();
    };
  }, [hidden]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // A closed (hidden) chat is out of sight: none of its shortcuts may act on it.
    if (hidden) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      // Close modal on Escape key
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (!event.ctrlKey) return;

      // Ctrl+R: restart conversation
      if (event.key === 'r') {
        event.preventDefault();
        silenceAssistant();
        setMessages([]);
        setView('chat');
        setShowMenu(false);
        return;
      }

      // Ctrl+H: toggle history / more options menu
      if (event.key === 'h') {
        event.preventDefault();
        setShowMenu((prev) => !prev);
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, hidden]);

  return (
    <div className="fixed z-50" style={{ right, bottom }} hidden={hidden} data-testid="alpha-chat-window">
      <div
        className={`relative flex flex-col rounded-[10px] bg-surface shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden max-h-[calc(100vh-4rem-0.5rem)] ${
          activeDrag ? 'select-none' : ''
        }`}
        style={{ width, height }}
      >
        {/* The top edge resizes the height: drag it, or focus it and use the arrow keys. */}
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label={t('alpha.resizeChatHeight')}
          aria-valuemin={CHAT_MIN_HEIGHT}
          aria-valuemax={maxHeight}
          aria-valuenow={height}
          // A focusable separator is the WAI-ARIA window-splitter pattern; jsx-a11y does not know it.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          data-testid="alpha-resize-top"
          className="group absolute left-3 right-0 top-0 z-10 flex h-2 cursor-ns-resize touch-none justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-on-primary"
          {...topEdgeHandlers}
        >
          <span
            aria-hidden="true"
            className={`mt-0.5 h-1 w-10 rounded-full bg-on-primary/60 transition-opacity ${
              activeDrag === 'top' || activeDrag === 'corner'
                ? 'opacity-100'
                : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
            }`}
          />
        </div>
        {/* The left edge resizes the width: drag it, or focus it and use the arrow keys. */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('alpha.resizeChatWidth')}
          aria-valuemin={CHAT_MIN_WIDTH}
          aria-valuemax={maxWidth}
          aria-valuenow={width}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          data-testid="alpha-resize-left"
          className="group absolute bottom-0 left-0 top-3 z-10 flex w-2 cursor-ew-resize touch-none items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
          {...leftEdgeHandlers}
        >
          <span
            aria-hidden="true"
            className={`ml-0.5 h-10 w-1 rounded-full bg-primary/50 transition-opacity ${
              activeDrag === 'left' || activeDrag === 'corner'
                ? 'opacity-100'
                : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
            }`}
          />
        </div>
        {/* The top-left corner resizes both at once (the two edges cover the keyboard). */}
        <div
          aria-hidden="true"
          data-testid="alpha-resize-corner"
          className="absolute left-0 top-0 z-20 h-3 w-3 cursor-nwse-resize touch-none"
          {...cornerHandlers}
        />
        {/* The header drags the window anywhere on screen (its buttons keep their own clicks). */}
        <div
          className={`flex select-none items-center justify-between bg-header p-3 text-on-primary touch-none ${
            activeDrag === 'move' ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          data-testid="alpha-chat-header"
          {...moveHandlers}
        >
          <div className="flex items-center">
            <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary">
              <Sparkles />
            </div>
            <div>
              <h2 className="font-medium text-base leading-none">
                {view === 'chat' ? t('alpha.messages') : t('alpha.title')}
              </h2>
              <div className="flex items-center">
                <div className="mr-1 h-2.5 w-2.5 rounded-full" style={{ backgroundColor: 'rgb(var(--color-online))' }}></div>
                <p className="text-xs py-1">{t('alpha.online')}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div ref={menuRef} className="relative">
              <Tooltip text={t('alpha.moreOptions')} position="bottom" align="left">
                <button onClick={() => setShowMenu(!showMenu)} aria-label={t('alpha.moreOptions')}>
                  <MoreHorizontal size={24} aria-hidden="true" />
                </button>
              </Tooltip>
              {showMenu && (
                <div className="absolute top-full right-0 mt-2 w-60 rounded-lg bg-surface py-2 shadow-lg ring-1 ring-border z-50">
                  <ul>
                    <li>
                      <button
                        onClick={() => {
                          setShowShortcuts(true);
                          setShowMenu(false);
                        }}
                        className="flex w-full items-center px-4 py-2 text-left text-sm text-text hover:bg-surface-alt"
                      >
                        <Keyboard size={16} className="mr-3 text-primary" />
                        {t('alpha.keyboardShortcuts')}
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => {
                          silenceAssistant();
                          resetThreadId();
                          setMessages([]);
                          setView('chat');
                          setShowMenu(false);
                        }}
                        className="flex w-full items-center px-4 py-2 text-left text-sm text-text hover:bg-surface-alt"
                      >
                        <RotateCw size={16} className="mr-3 text-primary" />
                        {t('alpha.restartConversation')}
                      </button>
                    </li>
                    {/*
                      "Previous conversations" is HIDDEN (2026-09-18). The entry
                      was a button with no handler at all — it opened nothing and
                      did nothing — so it read as a broken control. Removed from
                      the menu only: nothing about thread history is changed on
                      the backend (threads are still checkpointed and a thread id
                      still resumes a conversation), and this comes back the day
                      there is a view to open.
                    */}
                  </ul>
                </div>
              )}
            </div>
            <Tooltip
              text={isMuted ? t('alpha.unmuteAi') : t('alpha.muteAi')}
              position="bottom"
              align="right"
            >
              <button
                onClick={() => setIsMuted(!isMuted)}
                aria-label={isMuted ? t('alpha.unmuteAi') : t('alpha.muteAi')}
              >
                {isMuted ? (
                  <VolumeX size={24} aria-hidden="true" />
                ) : (
                  <Volume2 size={24} aria-hidden="true" />
                )}
              </button>
            </Tooltip>
            <Tooltip text={t('alpha.close')} position="bottom" align="right">
              <button onClick={onClose} aria-label={t('alpha.close')}>
                <X size={24} aria-hidden="true" />
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="flex-grow overflow-y-auto">
          {view === 'home' && (
            <>
              <div className="welcome-gradient p-6 text-on-primary">
                <h1 className="text-2xl font-semibold leading-[30px] mb-2">
                  {t('alpha.welcomeTitle')}
                </h1>
                <p className="text-sm font-normal leading-4">
                  {t('alpha.welcomeSubtitle')}
                </p>
              </div>
              <div className="@container">
                <div className="flex flex-col">
                  <div className="p-4">
                    <button
                      onClick={() => {
                        setView('chat');
                        setMessages([]);
                      }}
                      className="group flex w-full items-center justify-between border border-border bg-surface p-6 text-left ask-a-question-button"
                    >
                      <div className="flex items-center">
                        <div className="mr-4 transition-transform duration-200 group-hover:scale-110">
                          <AskAQuestionIcon />
                        </div>
                        <div>
                          <p className="font-medium transition-colors duration-200 group-hover:text-primary">
                            {t('alpha.askAQuestion')}
                          </p>
                          <p className="text-sm text-text-muted">
                            {t('alpha.aiPoweredSupport')}
                          </p>
                        </div>
                      </div>
                      <ArrowRight strokeWidth={3} className="text-primary transition-transform duration-200 group-hover:translate-x-1" />
                    </button>
                    {/* The Morning Briefing stays reachable after the card is dismissed:
                        the same question, the same shared briefing (a cache hit). */}
                    <button
                      onClick={() => askNow(MORNING_BRIEFING_QUESTION)}
                      data-testid="alpha-home-morning-briefing"
                      className="group mt-3 flex w-full items-center justify-between border border-border bg-surface p-4 text-left ask-a-question-button"
                    >
                      <div className="flex items-center">
                        <div className="mr-4 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-on-primary">
                          <Sparkles size={16} aria-hidden="true" />
                        </div>
                        <div>
                          <p className="font-medium transition-colors duration-200 group-hover:text-primary">
                            {t('alpha.briefing.title')}
                          </p>
                          <p className="text-sm text-text-muted">{t('alpha.briefing.homeDescription')}</p>
                        </div>
                      </div>
                      <ArrowRight strokeWidth={3} className="text-primary transition-transform duration-200 group-hover:translate-x-1" />
                    </button>
                    {/* The End-of-Shift Handover, reachable at any time — not only
                        when the card offers it near the end of a shift. */}
                    <button
                      onClick={() => askNow(SHIFT_HANDOVER_QUESTION)}
                      data-testid="alpha-home-shift-handover"
                      className="group mt-3 flex w-full items-center justify-between border border-border bg-surface p-4 text-left ask-a-question-button"
                    >
                      <div className="flex items-center">
                        <div className="mr-4 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-on-primary">
                          <ClipboardList size={16} aria-hidden="true" />
                        </div>
                        <div>
                          <p className="font-medium transition-colors duration-200 group-hover:text-primary">
                            {t('alpha.handover.title')}
                          </p>
                          <p className="text-sm text-text-muted">{t('alpha.handover.homeDescription')}</p>
                        </div>
                      </div>
                      <ArrowRight strokeWidth={3} className="text-primary transition-transform duration-200 group-hover:translate-x-1" />
                    </button>
                    <div className="mt-6">
                      <h3 className="mb-2 quick-questions-header">
                        {t('alpha.quickQuestions')}
                      </h3>
                      {quickQuestions.map((q) => (
                        <Accordion
                          key={q}
                          title={q}
                          titleClassName="quick-question-title transition-colors duration-200 group-hover:text-primary"
                          icon={
                            <HelpCircle
                              size={20}
                              className="text-primary"
                            />
                          }

                          onClick={() => {
                            const timestamp = new Date().toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            });
                            handleSendMessage(q, timestamp, { inputMode: 'text' });
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mt-6 h-full rounded-lg bg-surface-alt p-4 text-sm">
                      <div className="flex items-center">
                        <div className="mr-3 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-info/10">
                          <Info size={16} className="text-info" />
                        </div>
                        <h3 className="font-medium text-text">{t('alpha.proTip')}</h3>
                      </div>
                      <p className="mt-2 text-text-muted">
                        {t('alpha.proTipDescription')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {view === 'chat' && (
            <ChatView
              messages={messages}
              loading={loading}
              onSendMessage={handleSendMessage}
              isMuted={isMuted}
              listening={listening}
              voiceEnabled={voiceEnabled}
              {...(convUiBaseUrl !== undefined && { convUiBaseUrl })}
              startListening={async () => {
                await startAudioRecording();
                setListening(true);
              }}
              stopListening={async () => {
                const audioBlob = await stopAudioRecording();
                if (audioBlob && audioBlob.size > 0) {
                  handleSendAudio(audioBlob);
                }
                setListening(false);
              }}
              abortListening={() => {
                abortAudioRecording();
                setListening(false);
              }}
              onRestart={() => {
                silenceAssistant();
                resetThreadId();
                setMessages([]);
                setView('home');
              }}
              onToggleMute={() => {
                setIsMuted(!isMuted);
              }}
              onShowShortcuts={() => {
                setShowShortcuts(true);
              }}
            />
          )}
        </div>

        <div className="flex w-full justify-around border-t border-border bg-surface p-2">
          <button
            onClick={() => handleFooterNav('home')}
            className={`flex flex-col items-center rounded-lg px-4 py-2 ${
              view === 'home' ? 'text-primary' : 'text-text-muted'
            }`}
          >
            <Home size={20} />
            <span className="text-xs">{t('alpha.home')}</span>
          </button>
          <button
            onClick={() => handleFooterNav('chat')}
            className={`flex flex-col items-center rounded-lg px-4 py-2 ${
              view === 'chat' ? 'text-primary' : 'text-text-muted'
            }`}
          >
            <MessageSquare size={20} />
            <span className="text-xs">{t('alpha.messages')}</span>
          </button>
        </div>
        {showShortcuts && <KeyboardShortcutsModal onClose={() => setShowShortcuts(false)} />}
      </div>
    </div>
  );
};

export default AlphaModal;